import { Platform } from 'react-native';

export type WebTheme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const EVENT_NAME = 'smartsaas:theme';

function isWeb() {
  return Platform.OS === 'web' && typeof window !== 'undefined' && typeof document !== 'undefined';
}

export function getSystemTheme(): WebTheme {
  if (!isWeb()) return 'light';
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function getStoredTheme(): WebTheme | null {
  if (!isWeb()) return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

export function clearStoredTheme() {
  if (!isWeb()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getInitialTheme(): WebTheme {
  return getStoredTheme() ?? getSystemTheme();
}

export function applyTheme(theme: WebTheme, { persist = true }: { persist?: boolean } = {}) {
  if (!isWeb()) return;

  const alreadyApplied =
    document.documentElement.dataset.theme === theme &&
    document.documentElement.style.colorScheme === theme;

  // Use a single “source of truth” the browser understands:
  // - `data-theme` for app CSS
  // - `color-scheme` for native form controls / UA styling
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;

  if (persist) {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }

  if (!alreadyApplied) {
    try {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { theme } }));
    } catch {
      // ignore
    }
  }
}

export function subscribeThemeChanges(handler: (theme: WebTheme) => void) {
  if (!isWeb()) return () => {};

  const onCustom = (e: Event) => {
    const detail = (e as CustomEvent)?.detail;
    const theme = detail?.theme;
    if (theme === 'light' || theme === 'dark') handler(theme);
  };

  window.addEventListener(EVENT_NAME, onCustom as any);

  // Also react to system changes if user hasn't chosen an explicit theme.
  const media = window.matchMedia?.('(prefers-color-scheme: dark)');
  const onMedia = () => {
    // If user has stored preference, don't override it.
    if (getStoredTheme()) return;
    handler(getSystemTheme());
  };

  try {
    media?.addEventListener?.('change', onMedia);
  } catch {
    // Safari fallback
    media?.addListener?.(onMedia);
  }

  return () => {
    window.removeEventListener(EVENT_NAME, onCustom as any);
    try {
      media?.removeEventListener?.('change', onMedia);
    } catch {
      media?.removeListener?.(onMedia);
    }
  };
}

